import React, { useEffect, useState } from "react";
import { fabric } from "fabric";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentFile, setFile } from "../../redux/creators/fileCreators";
import {
  getCustomizeCanvas,
  setCustomizeCanvas,
} from "../../redux/creators/customizeCreators";
import { setShowCustomize } from "../../redux/reducers/threekitSlice";
import { TextTools, Toolbar, MobileTextTools } from "../../imports";
import "../../../../css/customize.css";
import styled from "styled-components";
import { device } from "../../../../utils/breakPoints";

/**
 * Entry point for Add Text Customizer
 *
 * @returns {Object}
 */
export default function CustomizeTools({ optionsUtility }) {
  const { file, canvas } = useSelector((state) => ({
    file: getCurrentFile(state),
    canvas: getCustomizeCanvas(state),
  }));
  const [isLoaded, setIsLoaded] = useState(false);
  const [headerColor, setHeaderColor] = useState(false);
  const dispatch = useDispatch();
  const addFile = (file) => dispatch(setFile(file));

  useEffect(() => {
    let fabricCanvas = new fabric.Canvas("customizeCanvas", {
      enableRetinaScaling: false,
      preserveObjectStacking: true,
    });

    // If no file is uploaded, set canvas size
    if (!file) {
      fabricCanvas.setDimensions({
        width: 3000,
        height: 3000,
      });
      // Else set the image
    } else {
      setImage(fabricCanvas);
    }
    dispatch(setCustomizeCanvas(fabricCanvas));
  }, []);

  const setGuide = () => {
    const UTILITY = optionsUtility;
    const ratio = UTILITY.dimensions.canHeight / UTILITY.dimensions.canWidth;
    // Calculate guide Height and width
    let newHeight, newWidth;
    if (canvas.height < canvas.width) {
      newHeight = canvas.height;
      newWidth = canvas.height / ratio;
    } else {
      newWidth = canvas.width;
      newHeight = canvas.width * ratio;
    }

    const rectangle = new fabric.Rect({
      name: "GUIDE",
      width: newWidth,
      height: newHeight,
      selectable: false,
      strokeWidth: 5,
      fill: "rgba(0,0,0,0)",
      stroke: "white",
      strokeDashArray: [10, 10],
      originX: "center",
      originY: "center",
      pointerEvents: "none",
    });
    rectangle.selectable = false;
    rectangle.evented = false;
    canvas.add(rectangle).centerObject(rectangle);
  };

  useEffect(() => {
    if (isLoaded) {
      setGuide();
      canvas.on("object:added", function (e) {
        const guide = canvas.getObjects().find((obj) => obj.name === "GUIDE");
        canvas.bringToFront(guide);
      });
      const guide = canvas.getObjects().find((obj) => obj.name === "GUIDE");
      // variables to store the info of whether the height or width of the guide has changed
      // Get the value of space between the guide and the full picture.
      let guideLine, heightOrWidth;
      if (guide.height != canvas.height) {
        guideLine = (canvas.height - guide.height) / 2;
        heightOrWidth = "height";
      } else if (guide.width != canvas.width) {
        guideLine = (canvas.width - guide.width) / 2;
        heightOrWidth = "width";
      }

      // Add event listener to canvas to check if objects go out of bounds
      canvas.on("object:moving", function (e) {
        const obj = e.target;
        const canvas = document.getElementById("customizeCanvas");
        const boundingRect = obj.getBoundingRect();
        if (heightOrWidth == "height") {
          if (
            boundingRect.left < 0 ||
            boundingRect.top < guideLine ||
            boundingRect.left + boundingRect.width > canvas.width ||
            boundingRect.top + boundingRect.height > canvas.height - guideLine
          ) {
            setHeaderColor(true);
          } else {
            setHeaderColor(false);
          }
        } else if (heightOrWidth == "width") {
          if (
            boundingRect.left < guideLine ||
            boundingRect.top < 0 ||
            boundingRect.left + boundingRect.width > canvas.width - guideLine ||
            boundingRect.top + boundingRect.height > canvas.height
          ) {
            setHeaderColor(true);
          } else {
            setHeaderColor(false);
          }
        }
      });
    }
  }, [isLoaded]);

  const setImage = (fabricCanvas) => {
    const fileUrl = URL.createObjectURL(file);
    new fabric.Image.fromURL(fileUrl, (image) => {
      fabricCanvas.setDimensions({
        width: image.width,
        height: image.height,
      });
      image.set({ scaleX: 1, scaleY: 1 });
      fabricCanvas.setBackgroundImage(image);
      fabricCanvas.renderAll();
      setIsLoaded(true);
    });
  };

  const deselectAll = () => {
    canvas.set("isDrawingMode", false);
    canvas.discardActiveObject().renderAll();
  };

  const saveFile = () => {
    const guide = canvas.getObjects().find((obj) => obj.name === "GUIDE");
    canvas.remove(guide);
    deselectAll();

    const fabricCanvas = document.getElementById("customizeCanvas");

    fabricCanvas.toBlob((blob) => {
      let file = new File([blob], "customFile.png", { type: "image/png" });
      addFile(file);
    }, "image/png");

    dispatch(setShowCustomize(false));
  };

  return (
    <CustomizeToolsContainer>
      <Header>
        <h2>Personalize Your Image</h2>
        <div>
          <h5 style={{ color: headerColor ? "red" : "black" }}>
            Please note, all text should be placed within the white dashed
            border. Anything outside the line will not print correctly.
          </h5>
          <h5>
            If you decide to change your image after you save your text, you
            will have to recreate your text.
          </h5>
        </div>
        <div>
          <CancelButton onClick={() => dispatch(setShowCustomize(false))}>
            Cancel
          </CancelButton>
          <SaveButton onClick={() => saveFile()}>Save Image</SaveButton>
        </div>
      </Header>
      <Content>
        <Toolbar />
        <div className="image-container">
          <canvas id="customizeCanvas" />
        </div>
        <div className="details-container">
          <div className="details-container-inner">
            <h3>Customize Text</h3>
            <TextTools />
          </div>
          <div className="details-container-mobile">
            <MobileTextTools />
          </div>
        </div>
      </Content>
    </CustomizeToolsContainer>
  );
}

const CustomizeToolsContainer = styled.div`
  inset: 0;
  margin: 0.25em;
  z-index: 10000;
  padding: 1em;
  text-align: left;
  position: fixed;
  border-radius: 0.5em;
  background: #ffffff;
  font-family: "sofia-pro";
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  @media ${device.laptopSm} {
    margin: 0;
    padding: 0.5em;
  }
`;
const Header = styled.div`
  color: #454545;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media ${device.laptopSm} {
    flex-wrap: wrap;
    flex-direction: column;
    justify-content: center;
    padding-bottom: 20px;
  }
`;
const CancelButton = styled.button`
  font-size: 16px;
  padding: 8px 20px;
  background: transparent;
  margin-left: 1em;
  border: none;
  text-decoration: underline;
  @media ${device.laptopSm} {
    margin: 0 0.25em;
  }
`;
const SaveButton = styled.button`
  font-size: 16px;
  padding: 8px 20px;
  background: transparent;
  margin-left: 1em;
  color: #ffffff;
  border: none;
  background-color: var(--color-wine);
  @media ${device.laptopSm} {
    margin: 0 0.25em;
  }
`;
const Content = styled.div`
  display: flex;
  height: 80vh;
  @media ${device.laptopSm} {
    flex-wrap: wrap;
    height: auto;
  }
`;
